<template>
  <v-container fluid class="dl-wizard-container">
    <div class="dl-wizard" data-testid="dl-wizard">
      <header class="dl-wizard__header">
        <h2 class="dl-wizard__title" data-testid="dl-wizard-title">
          {{ isEdit ? 'Edit decoration' : 'Add a decoration' }}
        </h2>
        <div class="dl-wizard__steps" data-testid="dl-wizard-steps">
          <span
            v-for="step in 3"
            :key="step"
            :class="[
              'dl-wizard__step',
              {
                'dl-wizard__step--active': currentStep === step,
                'dl-wizard__step--done': currentStep > step
              }
            ]"
            :data-testid="`dl-wizard-step-${step}`"
          >
            <span class="dl-wizard__step-num">{{ step }}</span>
            <span class="dl-wizard__step-label">{{ stepLabels[step - 1] }}</span>
          </span>
        </div>
      </header>

      <!-- ====================================================== -->
      <!-- EDIT MODE: legacy 1-step name/description form         -->
      <!-- Per WIZARD-FUNCTIONALITY-AUDIT.md NEEDS SPEC #1, the   -->
      <!-- edit-mode rebuild is deferred to Phase 3.1 / 3.3       -->
      <!-- (Decoration Detail page + GovernedEditModal). Until    -->
      <!-- those land, the legacy decoration_artifacts edit path  -->
      <!-- stays alive so the existing route                      -->
      <!-- /decoration-library/:id/edit does not 404.             -->
      <!-- ====================================================== -->
      <section
        v-if="isEdit"
        class="dl-wizard__body"
        data-testid="dl-wizard-edit-body"
      >
        <p class="dl-wizard__help" data-testid="dl-wizard-edit-notice">
          Editing legacy decoration metadata. The redesigned
          Decoration Detail page is on the way; until then, this
          surface stays minimal.
        </p>
        <label class="dl-field">
          <span class="dl-field__label">Name</span>
          <input
            v-model.trim="legacyForm.name"
            type="text"
            class="dl-field__input"
            data-testid="dl-wizard-name"
            maxlength="255"
          />
        </label>
        <label class="dl-field">
          <span class="dl-field__label">Description (optional)</span>
          <textarea
            v-model="legacyForm.description"
            class="dl-field__input"
            rows="3"
            data-testid="dl-wizard-description"
          />
        </label>
        <p
          v-if="submitError"
          class="dl-wizard__error"
          data-testid="dl-wizard-error"
        >
          {{ submitError }}
        </p>
      </section>

      <!-- ====================================================== -->
      <!-- ADD MODE: new 3-step flow (ADR 0045)                   -->
      <!-- Step 1: Upload artwork (file + name)                   -->
      <!-- Step 2: Process & locations (ProcessPicker)            -->
      <!-- Step 3: Assign client + review                         -->
      <!-- ====================================================== -->

      <!-- Step 1: Upload artwork -->
      <section
        v-if="!isEdit && currentStep === 1"
        class="dl-wizard__body"
        data-testid="dl-wizard-step-1-body"
      >
        <p class="dl-wizard__help">
          Upload the artwork file and give this decoration a name
          you'll recognize later in the library.
        </p>
        <label class="dl-field">
          <span class="dl-field__label">
            Artwork file <span class="dl-field__req">*</span>
          </span>
          <input
            ref="fileInput"
            type="file"
            class="dl-field__input"
            data-testid="dl-wizard-file"
            :accept="acceptedFileExtensions"
            @change="onFileSelected"
          />
          <span
            v-if="addForm.file"
            class="dl-field__help"
            data-testid="dl-wizard-file-name"
          >
            Selected: {{ addForm.file.name }}
          </span>
          <span class="dl-field__help">
            Accepted: {{ acceptedFileExtensionsLabel }}
          </span>
        </label>
        <label class="dl-field">
          <span class="dl-field__label">
            Decoration name <span class="dl-field__req">*</span>
          </span>
          <input
            v-model.trim="addForm.name"
            type="text"
            class="dl-field__input"
            data-testid="dl-wizard-name"
            maxlength="255"
            placeholder="What you'll see in the library"
          />
        </label>
      </section>

      <!-- Step 2: Process & locations -->
      <section
        v-if="!isEdit && currentStep === 2"
        class="dl-wizard__body"
        data-testid="dl-wizard-step-2-body"
      >
        <p class="dl-wizard__help">
          Tell us how this gets produced. The decorator uses this to
          spec the digitization work.
        </p>
        <ProcessPicker
          v-model="addForm.processId"
          :locked-method-slug="lockedMethodSlug"
          data-testid="dl-wizard-process-picker"
          @loaded="onProcessesLoaded"
          @load-error="onProcessesLoadError"
        />
        <p
          v-if="processLoadError"
          class="dl-wizard__error"
          data-testid="dl-wizard-process-error"
        >
          {{ processLoadError }}
        </p>
      </section>

      <!-- Step 3: Assign client + review -->
      <section
        v-if="!isEdit && currentStep === 3"
        class="dl-wizard__body"
        data-testid="dl-wizard-step-3-body"
      >
        <p class="dl-wizard__help">
          Assign the client who signs off. They'll get a magic-link
          proof email once digitizing completes.
        </p>
        <label class="dl-field">
          <span class="dl-field__label">Client</span>
          <select
            v-model="addForm.clientId"
            class="dl-field__input"
            data-testid="dl-wizard-client"
            :disabled="clientsLoading"
          >
            <option :value="null" data-testid="dl-wizard-client-none">
              -- No client yet (draft) --
            </option>
            <option
              v-for="c in clients"
              :key="c.id"
              :value="c.id"
              :data-testid="`dl-wizard-client-${c.id}`"
            >
              {{ c.name }}
            </option>
          </select>
          <span
            v-if="clientsLoading"
            class="dl-field__help"
            data-testid="dl-wizard-clients-loading"
          >
            Loading clients...
          </span>
          <span
            v-if="clientsLoadError"
            class="dl-field__help dl-field__help--error"
            data-testid="dl-wizard-clients-error"
          >
            {{ clientsLoadError }}
          </span>
        </label>

        <dl class="dl-review" data-testid="dl-wizard-review">
          <dt>Artwork</dt>
          <dd data-testid="dl-review-artwork">
            {{ addForm.file ? addForm.file.name : '(none)' }}
          </dd>
          <dt>Name</dt>
          <dd data-testid="dl-review-name">
            {{ addForm.name || '(missing)' }}
          </dd>
          <dt>Process</dt>
          <dd data-testid="dl-review-process">
            {{ selectedProcessDisplayName || '(not selected)' }}
          </dd>
          <dt>Client</dt>
          <dd data-testid="dl-review-client">
            {{ selectedClientName }}
          </dd>
        </dl>

        <p
          v-if="submitError"
          class="dl-wizard__error"
          data-testid="dl-wizard-error"
        >
          {{ submitError }}
        </p>
      </section>

      <footer class="dl-wizard__footer">
        <button
          v-if="!isEdit && currentStep > 1"
          type="button"
          class="dl-btn dl-btn--secondary"
          data-testid="dl-wizard-back"
          :disabled="submitting"
          @click="back"
        >
          Back
        </button>
        <button
          v-if="!isEdit && currentStep < 3"
          type="button"
          class="dl-btn dl-btn--primary"
          data-testid="dl-wizard-next"
          :disabled="!canAdvance"
          @click="next"
        >
          Continue
        </button>
        <button
          v-if="!isEdit && currentStep === 3"
          type="button"
          class="dl-btn dl-btn--primary"
          data-testid="dl-wizard-submit"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ submitting ? 'Saving...' : 'Create' }}
        </button>
        <button
          v-if="isEdit"
          type="button"
          class="dl-btn dl-btn--primary"
          data-testid="dl-wizard-submit"
          :disabled="!canSubmitEdit"
          @click="submit"
        >
          {{ submitting ? 'Saving...' : 'Save changes' }}
        </button>
        <button
          type="button"
          class="dl-btn dl-btn--link"
          data-testid="dl-wizard-cancel"
          :disabled="submitting"
          @click="cancel"
        >
          Cancel
        </button>
      </footer>
    </div>
  </v-container>
</template>

<script>
import axios from "@/axios";
import { getImageBaseUrl } from "@/utils/imageBaseUrl";
import {
  ARTWORK_UPLOAD_ERROR_EMPTY,
  isEmbroiderySource,
  isEmptyFile,
} from "@/utils/artworkUploadContract";
import ProcessPicker from "./ProcessPicker.vue";

/**
 * DecorationAddWizard.vue
 * =======================
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 3.6 wizard audit + updates.
 * Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
 *       04-decoration-detail.md (build steps 12-13).
 * Audit: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
 *        WIZARD-FUNCTIONALITY-AUDIT.md (P3-Q12 gate).
 * ADRs: ADR 0045 (process selection at creation), ADR 0035 (artwork
 *       lifecycle), ADR 0050 (artwork repository model), ADR 0017
 *       (multi-client decoration reuse).
 *
 * Two modes:
 *
 * 1. ADD mode (route ``/decoration-library/new``) -- new 3-step
 *    flow per the 2026-06-13 design package.
 *      Step 1: Upload artwork -- file + name
 *      Step 2: Process & locations -- ProcessPicker (4 rows from
 *              decoration_processes lookup; locations DEFERRED per
 *              audit NEEDS SPEC #3)
 *      Step 3: Assign client + review -- single-select dropdown
 *              with "-- No client yet (draft) --" sentinel
 *
 *    Submission flow:
 *      a) POST /api/upload_image (multipart) -> {uuid}
 *      b) GET /api/artwork_repositories/mine -> first row's id
 *      c) POST /api/artworks/pipeline with the F1.e body shape:
 *         { repository_id, process_id, file_url, original_filename,
 *           filename, image_url, image_mime_type }
 *      d) Redirect to /distributor/decoration-library
 *
 *    Client selection: pre-tag only -- the wizard does NOT call any
 *    approval-record endpoint per audit NEEDS SPEC #5. The
 *    selected client_id rides on the wizard state and surfaces in
 *    the review panel; the canonical "assign client" lives in the
 *    Approval workspace (Phase 4) where it has a real home.
 *
 * 2. EDIT mode (route ``/decoration-library/:id/edit``) -- the
 *    legacy decoration_artifacts edit path, untouched per audit
 *    NEEDS SPEC #1. The new Edit surface is GovernedEditModal on
 *    DecorationDetailPage (Phase 3.1 / 3.3). Once those land this
 *    edit branch should be replaced with a route redirect to
 *    /decoration-library/:id.
 *
 * Test expectations:
 *  - Vitest covers Add-mode flow end-to-end (mock axios for upload,
 *    artwork_repositories/mine, artworks/pipeline, clients).
 *  - Vitest covers ProcessPicker mount + selection emit.
 *  - Vitest covers Edit-mode legacy PATCH path stays alive.
 */

// Soft accept-list per audit NEEDS SPEC #2 -- backend upload_image
// enforces its own allowlist; the wizard only hints.
const ACCEPTED_FILE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".svg",
  ".ai",
  ".eps",
  ".pdf",
  // Embroidery stitch/design sources (PR-1 upload allowlist). A .dst/
  // .emb selection triggers the method<->format lock (PR-5): the
  // step-2 picker locks to embroidery. Kept in sync with the shared
  // artworkUploadContract allowlist + the backend upload gate.
  ".dst",
  ".emb",
];

export default {
  name: "DecorationAddWizard",
  components: { ProcessPicker },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      currentStep: 1,
      stepLabels: [
        "Upload artwork",
        "Process & locations",
        "Assign client",
      ],
      addForm: {
        file: null,
        name: "",
        processId: null,
        clientId: null,
      },
      // Edit mode keeps the legacy fields -- see component-level
      // comment for the rationale (audit NEEDS SPEC #1).
      legacyForm: {
        name: "",
        decoration_type: "embroidery",
        description: "",
      },
      // Captured from the ProcessPicker's ``loaded`` event so the
      // review panel can show the display_name without a second
      // fetch. Holds the {id, slug, display_name, ...} shape.
      _loadedProcesses: [],
      clients: [],
      clientsLoading: false,
      clientsLoadError: null,
      processLoadError: null,
      processesLoadedOnce: false,
      submitting: false,
      submitError: null,
    };
  },
  computed: {
    isEdit() {
      return this.id !== null && this.id !== undefined && this.id !== "";
    },
    acceptedFileExtensions() {
      return ACCEPTED_FILE_EXTENSIONS.join(",");
    },
    // PR-5 / WA-7c method<->format lock: a .dst/.emb artwork is an
    // embroidery stitch source and can ONLY be decorated by embroidery,
    // so lock the step-2 process picker to it. Any other (raster/vector)
    // file leaves the picker unconstrained (null). The backend guard
    // (handle_composite_bind) is the authority; this is the UX half.
    //
    // The same lock also lives on the product-editor bind surface
    // AddPlacementWizard.vue (Step-2 method select), which POSTs to the
    // same /api/placements/ endpoint. Both surfaces share the
    // isEmbroiderySource() helper so the format rule has one definition.
    lockedMethodSlug() {
      const name = this.addForm.file && this.addForm.file.name;
      return isEmbroiderySource(name) ? "embroidery" : null;
    },
    acceptedFileExtensionsLabel() {
      return ACCEPTED_FILE_EXTENSIONS.join(" - ");
    },
    canAdvance() {
      if (this.isEdit) return false;
      if (this.currentStep === 1) {
        return Boolean(this.addForm.file) && Boolean(this.addForm.name);
      }
      if (this.currentStep === 2) {
        return Boolean(this.addForm.processId);
      }
      return true;
    },
    canSubmit() {
      if (this.isEdit) return false;
      return (
        !this.submitting &&
        Boolean(this.addForm.file) &&
        Boolean(this.addForm.name) &&
        Boolean(this.addForm.processId)
      );
    },
    canSubmitEdit() {
      return !this.submitting && Boolean(this.legacyForm.name);
    },
    selectedProcessDisplayName() {
      const proc = this._loadedProcesses.find(
        (p) => p.id === this.addForm.processId,
      );
      return proc ? proc.display_name : "";
    },
    selectedClientName() {
      if (!this.addForm.clientId) return "(none -- draft)";
      const c = this.clients.find((x) => x.id === this.addForm.clientId);
      return c ? c.name : `(client #${this.addForm.clientId})`;
    },
  },
  async mounted() {
    this.$store.state.title = this.isEdit
      ? "Edit decoration"
      : "Add a decoration";
    this.$store.state.topTitle = false;
    if (this.isEdit) {
      await this.loadExisting();
    } else {
      // Pre-fetch the client list -- the dropdown shows on step 3
      // but starting the fetch early keeps the step transition
      // snappy. Failures are non-fatal; the picker stays usable
      // with the "no client" sentinel.
      this.fetchClients();
    }
  },
  unmounted() {
    this.$store.state.title = "";
    this.$store.state.topTitle = false;
  },
  methods: {
    next() {
      if (this.canAdvance && this.currentStep < 3) this.currentStep += 1;
    },
    back() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },
    cancel() {
      this.$router.push("/distributor/decoration-library");
    },
    onFileSelected(evt) {
      const file =
        evt && evt.target && evt.target.files && evt.target.files[0];
      // A Google Drive / iCloud / OneDrive placeholder arrives with the
      // right name and ZERO bytes. Catch it at PICK time so the doomed
      // upload never leaves the browser -- the server's honest 400 was
      // being surfaced as a bogus timeout. Root cause + copy live in
      // utils/artworkUploadContract.js.
      if (isEmptyFile(file)) {
        this.addForm.file = null;
        this.submitError = ARTWORK_UPLOAD_ERROR_EMPTY;
        return;
      }
      this.submitError = null;
      this.addForm.file = file || null;
    },
    onProcessesLoaded(rows) {
      this._loadedProcesses = Array.isArray(rows) ? rows : [];
      this.processesLoadedOnce = true;
    },
    onProcessesLoadError(err) {
      this.processLoadError =
        (err && err.message) ||
        "Could not load decoration processes. Please retry.";
    },
    async fetchClients() {
      this.clientsLoading = true;
      this.clientsLoadError = null;
      try {
        const resp = await axios.get("/api/clients/");
        const rows = this._normalizeClientsResponse(resp && resp.data);
        this.clients = rows;
      } catch (err) {
        this.clientsLoadError =
          (err && err.message) ||
          "Could not load clients. You can still create a draft.";
        this.clients = [];
      } finally {
        this.clientsLoading = false;
      }
    },
    _normalizeClientsResponse(body) {
      if (!body) return [];
      if (Array.isArray(body)) return body;
      if (Array.isArray(body.clients)) return body.clients;
      if (Array.isArray(body.data)) {
        return body.data.map((entry) => ({
          id: entry.id,
          ...(entry.attributes || {}),
        }));
      }
      return [];
    },
    async loadExisting() {
      try {
        const result = await this.$store.dispatch(
          "jv/get",
          `decoration_artifacts/${this.id}`,
        );
        const data =
          result && result._jv ? result : Object.values(result || {})[0];
        if (data) {
          this.legacyForm.name = data.name || "";
          this.legacyForm.decoration_type =
            data.decoration_type || "embroidery";
          this.legacyForm.description = data.description || "";
        }
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      }
    },
    async submit() {
      if (this.isEdit) {
        await this._submitEdit();
      } else {
        await this._submitAdd();
      }
    },
    async _submitEdit() {
      this.submitting = true;
      this.submitError = null;
      const payload = {
        name: this.legacyForm.name,
        decoration_type: this.legacyForm.decoration_type,
        description: this.legacyForm.description || null,
      };
      try {
        await axios.patch(
          `/api/decoration-artifacts/${this.id}`,
          payload,
        );
        this.$router.push("/distributor/decoration-library");
      } catch (err) {
        this.submitError =
          (err && err.message) || "Could not save decoration.";
        this.$store.dispatch("raiseError", err);
      } finally {
        this.submitting = false;
      }
    },
    async _submitAdd() {
      this.submitting = true;
      this.submitError = null;
      try {
        // Step (a): multipart upload to S3 via the shared
        // upload_image endpoint -- same pattern the artworks store
        // uploadArtwork() uses.
        const formData = new FormData();
        formData.append("file", this.addForm.file, this.addForm.file.name);
        const uploadRes = await axios.post("/api/upload_image", formData, {
          // Let axios derive multipart boundary; the default
          // Content-Type breaks server-side multipart parsing.
        });
        const uuid =
          (uploadRes && uploadRes.data && uploadRes.data.uuid) || null;
        if (!uuid) {
          throw new Error(
            "Upload succeeded but returned no UUID. Please retry.",
          );
        }
        const fileUrl = this._buildImageUrl(uuid);

        // Step (b): resolve the caller's repository. The F-3 /mine
        // alias returns the tenant's repos ordered by id; pick the
        // first one. Distributors today have a single auto-seeded
        // repository per tenant.
        const reposRes = await axios.get(
          "/api/artwork_repositories/mine",
        );
        const repos =
          (reposRes && reposRes.data && reposRes.data.repositories) || [];
        if (!repos.length) {
          throw new Error(
            "No artwork repository is available for this tenant. " +
              "Contact platform support.",
          );
        }
        const repositoryId = repos[0].id;

        // Step (c): F1.e service-driven create.
        const body = {
          repository_id: repositoryId,
          process_id: this.addForm.processId,
          file_url: fileUrl,
          original_filename: this.addForm.file.name,
          filename: this.addForm.name,
          image_url: fileUrl,
          image_mime_type: this.addForm.file.type || null,
        };
        await axios.post("/api/artworks/pipeline", body);

        // Step (d): redirect. Client linkage (addForm.clientId)
        // is intentionally not sent -- per audit NEEDS SPEC #5,
        // client assignment lives in the Approval workspace
        // (Phase 4); the picker here is a pre-tag for UX only.
        this.$router.push("/distributor/decoration-library");
      } catch (err) {
        this.submitError =
          (err && err.message) || "Could not create decoration.";
        this.$store.dispatch("raiseError", err);
      } finally {
        this.submitting = false;
      }
    },
    _buildImageUrl(uuid) {
      // Shared base-URL resolution (src/utils/imageBaseUrl.js) so the
      // S3/MinIO prefix comes from ONE source of truth and is inlined
      // by webpack DefinePlugin.
      return `${getImageBaseUrl()}${uuid}`;
    },
  },
};
</script>

<style scoped>
.dl-wizard-container {
  max-width: 720px;
}
.dl-wizard {
  background: var(--tf-bg);
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.dl-wizard__header {
  margin-bottom: 24px;
}
.dl-wizard__title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 16px;
}
.dl-wizard__steps {
  display: flex;
  gap: 24px;
  margin-bottom: 8px;
}
.dl-wizard__step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.5);
}
.dl-wizard__step--active {
  color: #1976d2;
  font-weight: 600;
}
.dl-wizard__step--done {
  color: var(--tf-success-dark);
}
.dl-wizard__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid currentColor;
  font-size: 13px;
}
.dl-wizard__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}
.dl-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.dl-field__label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
}
.dl-field__req {
  color: #d32f2f;
  margin-left: 2px;
}
.dl-field__input {
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  font-size: 14px;
}
.dl-field__help {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}
.dl-field__help--error {
  color: #d32f2f;
}
.dl-wizard__help {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  margin-bottom: 12px;
}
.dl-review {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 12px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 16px;
  margin-top: 8px;
}
.dl-review dt {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}
.dl-review dd {
  margin: 0;
}
.dl-wizard__error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 12px;
}
.dl-wizard__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 16px;
}
.dl-btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 14px;
  cursor: pointer;
}
.dl-btn--primary {
  background: #1976d2;
  color: var(--tf-bg);
}
.dl-btn--primary:disabled {
  background: rgba(25, 118, 210, 0.4);
  cursor: not-allowed;
}
.dl-btn--secondary {
  background: var(--tf-bg);
  border-color: rgba(0, 0, 0, 0.23);
  color: #1976d2;
}
.dl-btn--link {
  background: none;
  border: none;
  color: rgba(0, 0, 0, 0.6);
}
</style>
